import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';
// Utils
import { showToast } from '../utils/ToastsUtil';
import { isNetworkError, readListFromSnapshot, readListSnapshotFirst, saveListToSnapshot, withPendingListItems } from '../utils/offline/OfflineRead';
import ActionsUtil from '../utils/ActionsUtil';
import { canUseFieldWrite, submitFieldWrite } from '../utils/offline/FieldWrite';
import { isLocalId, newLocalNumericId } from '../utils/offline/IdMap';
import { getOfflineServices } from '../utils/offline/OfflineServices';
import { cleanupRemovedOps } from '../utils/offline/LocalCleanup';
import { OP_STATUS } from '../utils/offline/Outbox';
import i18n from '../locales/i18n';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class ActionsService {
    static addActionOccurrences({ projectId, actionId, date, isUrgent, customFields, elements }, successToast = 'action_added', errorToast = 'action_addition_failed') {
        if (canUseFieldWrite()) return ActionsService.addActionOccurrencesThroughOutbox({ projectId, actionId, date, isUrgent, customFields, elements }, successToast, errorToast);
        return Axios.post(BACKEND_ENDPOINT + 'actions/occurrences', { projectId, actionId, date, isUrgent, customFields, elements }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (isOnline()) showToast(errorToast);
            return;
        });
    }

    // Par la file : une occurrence locale par élément (id négatif), de même forme que celles de l'API (date en chaîne), remplacée par celle du serveur
    // à l'acceptation (ProjectMap.handleSyncEvent) ; les éléments créés sur l'appareil partent avant
    static addActionOccurrencesThroughOutbox(creation, successToast, errorToast) {
        const localIds = {};
        const occurrences = creation.elements.map(element => {
            const id = newLocalNumericId();
            localIds[element.elementId] = id;
            return {
                id, projectId: creation.projectId, actionId: creation.actionId, elementId: element.elementId, elementType: element.elementType,
                date: new Date(creation.date).toISOString(), status: null, validationDate: null, comment: '', customPrice: null, customFields: creation.customFields,
                isUrgent: creation.isUrgent, seriesId: null, replantingDate: null
            };
        });
        return submitFieldWrite({
            op: {
                type: 'occurrences.create', projectId: creation.projectId, target: { entity: 'occurrences', id: uuidv4() },
                touches: occurrences.map(occurrence => `occurrence:${occurrence.id}`),
                refs: creation.elements.map(element => ({ entity: 'element', id: element.elementId })),
                payload: { creation, localIds }
            },
            buildLocalData: () => ({ occurrences, existing: [], histories: [] })
        }).then(response => {
            if (successToast) showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.isLocal ? { ...response.data, isLocal: true } : response.data;
        }, () => {
            showToast(errorToast);
            return;
        });
    }

    // fields : champs modifiés, seuls recopiés par l'API (un collègue qui a modifié les autres n'est pas écrasé) ; vide, rien n'est envoyé.
    // localOccurrences : occurrences complètes rendues sans réponse de l'API, quand celles envoyées ne portent que les champs personnalisés
    // modifiés (sinon le store et la copie perdraient les autres)
    static updateActionOccurrences(projectId, occurrences, { fields = null, deathCauseId = null, cancelPendingRecurrences = false, successToast = 'action_updated', errorToast = 'action_update_failed', localOccurrences = null } = {}) {
        if (fields && !fields.length) return Promise.resolve({ occurrences: localOccurrences || occurrences, histories: [], trees: [] });
        if (canUseFieldWrite()) return ActionsService.updateActionOccurrencesThroughOutbox(projectId, occurrences, { fields, deathCauseId, cancelPendingRecurrences, successToast, errorToast, localOccurrences });
        return Axios.put(`${BACKEND_ENDPOINT}actions/occurrences?projectId=${projectId}&deathCauseId=${deathCauseId || 0}&cancelPendingRecurrences=${cancelPendingRecurrences ? 'true' : 'false'}${fields ? `&fields=${fields.join(',')}` : ''}`, occurrences, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (isOnline()) showToast(errorToast);
            return;
        });
    }

    // Par la file : la réponse locale reprend les occurrences envoyées, que les appelants fusionnent comme celles de l'API ; une action
    // létale (abattage…) ne tue l'arbre qu'à l'acceptation, c'est le serveur qui l'applique
    static updateActionOccurrencesThroughOutbox(projectId, occurrences, { fields, deathCauseId, cancelPendingRecurrences, successToast, errorToast, localOccurrences }) {
        const copies = occurrences.map(occurrence => ({ ...occurrence }));
        const localCopies = localOccurrences ? localOccurrences.map(occurrence => ({ ...occurrence })) : copies;
        return submitFieldWrite({
            op: {
                type: 'occurrences.update', projectId, target: { entity: 'occurrences', id: uuidv4() },
                touches: copies.map(occurrence => `occurrence:${occurrence.id}`),
                refs: copies.filter(occurrence => isLocalId('occurrence', occurrence.id)).map(occurrence => ({ entity: 'occurrence', id: occurrence.id })),
                payload: { occurrences: copies, fields, deathCauseId, cancelPendingRecurrences }
            },
            buildLocalData: () => ({ occurrences: localCopies, histories: [], trees: [] })
        }).then(response => {
            if (successToast) showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.isLocal ? { ...response.data, isLocal: true } : response.data;
        }, () => {
            showToast(errorToast);
            return;
        });
    }

    static deleteActionOccurrences(projectId, ids, successToast = 'action_deleted', errorToast = 'action_deletion_failed') {
        if (canUseFieldWrite()) return ActionsService.deleteActionOccurrencesThroughOutbox(projectId, ids, successToast, errorToast);
        return Axios.delete(`${BACKEND_ENDPOINT}actions/occurrences?projectId=${projectId}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (isOnline()) showToast(errorToast);
            return;
        });
    }

    // Par la file : une occurrence créée sur l'appareil et jamais envoyée est retirée de sa création, les autres partent dans une
    // suppression ; les photos jamais envoyées de ces occurrences sont retirées (l'API supprime déjà les photos des occurrences)
    static async deleteActionOccurrencesThroughOutbox(projectId, ids, successToast, errorToast) {
        const services = getOfflineServices();
        const idSet = new Set(ids);
        const photoOps = [];
        for (const op of await services.outbox.list())
            if (op.type === 'photo.upload' && op.status === OP_STATUS.PENDING && !op.attempts && idSet.has((await services.photos.get(op.payload.photoId))?.recurrenceId)) photoOps.push(op);
        for (const op of photoOps) {
            await cleanupRemovedOps(services, await services.outbox.removeWithDependents(op.id));
            await services.syncLog?.add({ kind: 'discarded', projectId, opType: op.type, targetKey: op.targetKey, label: i18n.t("Ajout d'une photo"), reason: 'deleted_on_server' });
        }
        const cancelledIds = [];
        for (const id of ids.filter(id => isLocalId('occurrence', id))) {
            const { cancelled, removedOps } = await services.outbox.cancelLocalOccurrence(id);
            await cleanupRemovedOps(services, removedOps);
            if (cancelled) cancelledIds.push(id);
        }
        const remaining = ids.filter(id => !cancelledIds.includes(id));
        const localData = { removedIds: ids, histories: [] };
        const promise = remaining.length
            ? submitFieldWrite({
                op: {
                    type: 'occurrences.delete', projectId, target: { entity: 'occurrences', id: uuidv4() },
                    touches: remaining.map(id => `occurrence:${id}`),
                    refs: remaining.filter(id => isLocalId('occurrence', id)).map(id => ({ entity: 'occurrence', id })),
                    payload: { ids: remaining }
                },
                buildLocalData: () => localData
            })
            : Promise.resolve({ data: localData, isLocal: true });
        return promise.then(response => {
            if (successToast) showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.isLocal ? { ...localData, isLocal: true } : response.data;
        }, () => {
            showToast(errorToast);
            return;
        });
    }

    // Par la file : les occurrences de l'élément ajouté sont générées par le serveur à l'envoi ; la série locale reçoit l'élément
    static addActionSeriesElementsThroughOutbox(projectId, seriesId, elements, successToast, errorToast, group) {
        const series = group?.series;
        const updatedSeries = series && { ...series, elements: [...series.elements.filter(element => !elements.some(added => added.elementId === element.elementId)), ...elements.map(element => ({ seriesId, ...element }))] };
        return submitFieldWrite({
            op: {
                type: 'series.addElements', projectId, target: { entity: 'series', id: seriesId },
                refs: elements.map(element => ({ entity: 'element', id: element.elementId })),
                payload: { seriesId, elements }
            },
            buildLocalData: () => ({ series: updatedSeries, occurrences: [], histories: [] })
        }).then(response => {
            if (successToast) showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.isLocal ? { ...response.data, isLocal: true } : response.data;
        }, () => {
            showToast(errorToast);
            return;
        });
    }

    // Comme le serveur : l'élément quitte la série, ses occurrences à faire de la série disparaissent, les autres restent
    static deleteActionSeriesElementsThroughOutbox(projectId, seriesId, elementIds, successToast, errorToast, group) {
        const series = group?.series;
        const updatedSeries = series && { ...series, elements: series.elements.filter(element => !elementIds.includes(element.elementId)) };
        const removedIds = (group?.occurrences || []).filter(occurrence => !occurrence.status && elementIds.includes(occurrence.elementId)).map(occurrence => occurrence.id);
        return submitFieldWrite({
            op: { type: 'series.removeElements', projectId, target: { entity: 'series', id: seriesId }, payload: { seriesId, elementIds } },
            buildLocalData: () => ({ series: updatedSeries, removedIds, histories: [] })
        }).then(response => {
            if (successToast) showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.isLocal ? { ...response.data, isLocal: true } : response.data;
        }, () => {
            showToast(errorToast);
            return;
        });
    }

    static addActionSeries({ projectId, actionId, startDate, endDate, recurrence, isUrgent, customFields, elements }) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/series', { projectId, actionId, startDate, endDate, recurrence, isUrgent, customFields, elements }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('action_addition_failed');
            return;
        });
    }

    static updateActionSeries({ id, projectId, endDate, isUrgent }) {
        return Axios.put(BACKEND_ENDPOINT + 'actions/series', { id, projectId, endDate, isUrgent }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_updated');
            return response.data;
        }, () => {
            if (isOnline()) showToast('action_update_failed');
            return;
        });
    }

    // group : groupe connu de l'appelant (série et occurrences), pour la réponse locale du hors-ligne
    static addActionSeriesElements(projectId, seriesId, elements, successToast = 'action_linked_single', errorToast = 'action_link_failed', group = null) {
        if (canUseFieldWrite()) return ActionsService.addActionSeriesElementsThroughOutbox(projectId, seriesId, elements, successToast, errorToast, group);
        return Axios.post(BACKEND_ENDPOINT + 'actions/series/elements', { seriesId, projectId, elements }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (isOnline()) showToast(errorToast);
            return;
        });
    }

    static deleteActionSeriesElements(projectId, seriesId, elementIds, successToast = 'action_unlinked', errorToast = 'action_unlink_failed', group = null) {
        if (canUseFieldWrite()) return ActionsService.deleteActionSeriesElementsThroughOutbox(projectId, seriesId, elementIds, successToast, errorToast, group);
        return Axios.delete(BACKEND_ENDPOINT + 'actions/series/elements', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { seriesId, projectId, elementIds }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (isOnline()) showToast(errorToast);
            return;
        });
    }

    static deleteActionSeries(projectId, id) {
        return Axios.delete(`${BACKEND_ENDPOINT}actions/series?projectId=${projectId}&id=${id}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_deleted');
            return response.data;
        }, () => {
            if (isOnline()) showToast('action_deletion_failed');
            return;
        });
    }

    // Opérations sur un groupe de l'adaptateur (série, ou action ponctuelle un jour donné)
    static addElementsToGroup(projectId, group, elements, successToast = 'action_linked_single', errorToast = 'action_link_failed') {
        return group.seriesId
            ? ActionsService.addActionSeriesElements(projectId, group.seriesId, elements, successToast, errorToast, group)
            : ActionsService.addActionOccurrences({ projectId, actionId: group.actionId, date: group.startDate, isUrgent: group.isUrgent, customFields: null, elements }, successToast, errorToast);
    }

    // Modification d'une série : date de fin et urgence (les occurrences ponctuelles passent par updateActionOccurrences)
    static updateGroup(projectId, group, { endDate, isUrgent }) {
        return ActionsService.updateActionSeries({ id: group.seriesId, projectId, endDate, isUrgent })
            .then(response => response && { changes: { created: response.created, updated: response.updated, removedIds: response.removedIds }, seriesChanges: { updated: [response.series] }, histories: [] });
    }

    static removeElementsFromGroup(projectId, group, elementIds, successToast = 'action_unlinked', errorToast = 'action_unlink_failed') {
        if (group.seriesId) return ActionsService.deleteActionSeriesElements(projectId, group.seriesId, elementIds, successToast, errorToast, group);
        const ids = group.occurrences.filter(occurrence => elementIds.includes(occurrence.elementId) && !occurrence.status).map(occurrence => occurrence.id);
        return ActionsService.deleteActionOccurrences(projectId, ids, successToast, errorToast);
    }

    // fallback : copie relue sans réseau (faux pour un rafraîchissement, comme EventsService.getProjectEvents)
    static async getActionOccurrences(projectId, { fallback = true } = {}) {
        // Copie locale d'abord pour un projet ouvert depuis la copie, en repli sans réseau ; tenue à jour par ProjectMap.saveListsToSnapshot
        const fromSnapshot = await readListSnapshotFirst(projectId, 'actionOccurrences');
        if (fromSnapshot !== undefined) return fromSnapshot;
        return Axios.get(BACKEND_ENDPOINT + 'actions/occurrences/' + projectId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(async response => {
            const data = await withPendingListItems(projectId, 'actionOccurrences', response.data);
            saveListToSnapshot(projectId, 'actionOccurrences', data);
            return data;
        }, async (error) => {
            if (fallback && isNetworkError(error)) {
                const cached = await readListFromSnapshot(projectId, 'actionOccurrences');
                if (cached !== undefined) return cached;
            }
            if (isOnline()) showToast('action_loading_failed');
            return;
        });
    }

    static getActions() {
        return Axios.get(BACKEND_ENDPOINT + 'actions/', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('action_loading_failed');
            return;
        });
    }

    static exportActionAsPDF(group, actionLabel, image, addPrices) {
        const occurrenceIds = group.occurrences.map(occurrence => occurrence.id);
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportPDF', { occurrenceIds, image, recurrenceInfos: ActionsUtil.getRecurrencesInfos(group), addPrices }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            const filename = 'Fiche action_' + actionLabel + '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', 'action', `"${actionLabel}"`);
        }, () => {
            if (isOnline()) showToast('action_pdf_export_failed');
        });
    }

    static exportBacklogAsPDF(data, toastCustomTexts) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportPDF/backlog/', data, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            const filename = data.groupedBy
                ? `Fiche actions_${data.startDate}_${data.endDate}.pdf`.replaceAll('/', '-')
                : `${data.reference}_Actions.pdf`;
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('backlog_pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (isOnline()) showToast('backlog_pdf_export_failed', ...toastCustomTexts);
        });
    }

    static exportBacklogAsXLSX(baseProject, rows, groupedBy, customFields, columnsToRender) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportXLSX/backlog/', { baseProjectId: baseProject.id, rows, groupedBy, customFields, columnsToRender }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${baseProject.label} - ${i18n.t("Actions")}.xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static exportAnnualBudgetAsXLSX(baseProject, startDate, endDate, priceListId) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportXLSX/annualBudget/', { baseProjectId: baseProject.id, startDate, endDate, priceListId }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${baseProject.label} - ${i18n.t("Actions")} (${i18n.t("Budget annuel")}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static addAction(action) {
        return Axios.post(BACKEND_ENDPOINT + 'admin/actions/', action, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('action_addition_failed');
            return;
        });
    }

    static updateActions(actions) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/actions/', actions, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('actions_updated');
            return response.status;
        }, () => {
            if (isOnline()) showToast('actions_update_failed');
            return;
        });
    }

    static deleteActions(ids) {
        return Axios.delete(BACKEND_ENDPOINT + 'admin/actions/', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids: ids }
        }).then(response => {
            return response.status;
        }, () => {
            showToast(ids.length > 1 ? 'actions_deletion_failed' : 'action_deletion_failed');
            return;
        });
    }

    static getActionHistory(elementId) {
        return Axios.get(BACKEND_ENDPOINT + 'actions/history/?elementId=' + elementId, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('element_history_loading_failed');
            return;
        });
    }
}